import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, CheckCircle2Icon, QrCodeIcon, Image as ImageIcon, StoreIcon, ZapIcon, ChevronDownIcon } from 'lucide-react';
import { useRaastTheme, slideVariants, PAY_FROM_ACCOUNTS } from './RaastShared';
import { TransactionReceipt } from '../TransactionReceipt';
interface RaastQrTillProps {
  onBack: () => void;
  onHome: () => void;
  onTransfer?: () => void;
}
type Step = 'scan' | 'entry' | 'review' | 'otp' | 'success';
type EntryOption = 'Till Code' | 'VPA' | 'Merchant ID' | 'Free Text' | 'Merchant Account Number';
export function RaastQrTill({
  onBack,
  onHome,
  onTransfer
}: RaastQrTillProps) {
  const {
    bgStyle,
    cardBg,
    inputBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('scan');
  const [direction, setDirection] = useState(1);
  const [topTab, setTopTab] = useState<'scan' | 'receive'>('scan');
  // QR Mode Simulation
  const [qrMode, setQrMode] = useState<'static' | 'dynamic'>('static');
  const [receiveQrMode, setReceiveQrMode] = useState<'static' | 'dynamic'>('static');
  const [receiveAmount, setReceiveAmount] = useState('');
  // "With Amount" toggle for the Pay To Merchant / receive QR (image 3)
  const [withAmount, setWithAmount] = useState(true);
  const [flashOn, setFlashOn] = useState(false);
  // Entry State
  const [entryOption, setEntryOption] = useState<EntryOption>('Till Code');
  const [tillNumber, setTillNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [payFromAccount, setPayFromAccount] = useState(PAY_FROM_ACCOUNTS[0]);
  const [showPayFromDropdown, setShowPayFromDropdown] = useState(false);
  // Review State
  const [savePayee, setSavePayee] = useState(false);
  const [purpose, setPurpose] = useState('Merchant Payment');
  const [comments, setComments] = useState('');
  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  const [payeeNickname, setPayeeNickname] = useState('');
  const [showPurposeDropdown, setShowPurposeDropdown] = useState(false);
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  useEffect(() => {
    if (step === 'scan' && topTab === 'scan') {
      const scanTimer = setTimeout(() => {
        // A scanned merchant QR carries a merchant-fixed amount, so jump
        // straight to the Review Payment screen with the details prefilled.
        setQrMode('dynamic');
        setTillNumber('887654');
        setAmount('2500');
        navigateTo('review');
      }, 2000);
      return () => clearTimeout(scanTimer);
    }
  }, [step, topTab]);
  useEffect(() => {
    if (step === 'otp') {
      const interval = setInterval(() => {
        setTimer(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`qr-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (newOtp.every(v => v !== '')) {
      setTimeout(() => navigateTo('success'), 350);
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`qr-otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between z-10 relative" data-id="element-2481" data-mp="1fh3b33b581ja">
      <button onClick={onBackClick} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${textPri}`} aria-label="Back" data-id="element-2482" data-mp="852y119zua6w">
        <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2483" data-mp="452h99zf9mwo" />
        <span className={`text-[16px] font-normal ${textPri}`} data-id="element-2484" data-mp="1rymgi1ghhooo">{title}</span>
      </button>
      <div className="flex-1" data-id="element-2485" data-mp="1wqntxqpe1lga" />
      <button onClick={onHome} className={`p-2 -mr-2 ${isDark || isPlatinum ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-2486" data-mp="1wrp31pmufxvd">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2487" data-mp="1n1mcdw372w07" />
      </button>
    </div>;
  const renderScan = () => <motion.div key="scan" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2488" data-mp="2argogbfowgje">
      {renderHeader(topTab === 'scan' ? 'Scan QR' : 'Pay To Merchant', onBack)}

      <div className="px-6 mb-6" data-id="element-2489" data-mp="25pre6y0ak6wd">
        <div className={`flex p-1 rounded-lg ${isDark || isPlatinum ? 'bg-[#3A3A3C]' : 'bg-[#EFEDEB]'}`} data-id="element-2490" data-mp="25sx2ib13j4r1">
          <button onClick={() => setTopTab('scan')} className={`flex-1 py-2.5 rounded-lg text-[13px] font-medium transition-all ${topTab === 'scan' ? isDark || isPlatinum ? 'bg-[#2A2A2C] text-[#F37024]' : 'bg-white text-[#F37024] shadow-sm' : textSec}`} data-id="element-2491" data-mp="ucf3c3646o0p">
            Scan QR
          </button>
          <button onClick={() => setTopTab('receive')} className={`flex-1 py-2.5 rounded-lg text-[13px] font-medium transition-all ${topTab === 'receive' ? isDark || isPlatinum ? 'bg-[#2A2A2C] text-[#F37024]' : 'bg-white text-[#F37024] shadow-sm' : textSec}`} data-id="element-2492" data-mp="wp97ebxwuadw">
            My QR to Receive
          </button>
        </div>
      </div>

      {topTab === 'scan' ? <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8" data-id="element-2493" data-mp="dshq281xi5ch">
          <div className="relative w-64 h-64 mb-8" data-id="element-2494" data-mp="8zybp8vf366m">
            {/* Scanner Frame */}
            <div className="absolute inset-0 border-2 border-[#F37024]/30 rounded-3xl overflow-hidden" data-id="element-2495" data-mp="3hfzw4q3w8cmq">
              <div className="w-full h-full bg-black/5" data-id="element-2496" data-mp="3ciht1gck4rmn" />
              {/* Animated scanning line */}
              <motion.div animate={{
            top: ['0%', '100%', '0%']
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} className="absolute left-0 right-0 h-1 bg-[#F37024] shadow-[0_0_10px_#F37024]" data-id="element-2497" data-mp="5zeoy40rkb72" />
            </div>
            {/* Corner markers */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-[#F37024] rounded-tl-3xl" data-id="element-2498" data-mp="3j8suhlepn6b6" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[#F37024] rounded-tr-3xl" data-id="element-2499" data-mp="3nanwks4gtnq0" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[#F37024] rounded-bl-3xl" data-id="element-2500" data-mp="11d6nlb5wfwn" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-[#F37024] rounded-br-3xl" data-id="element-2501" data-mp="17hyoks8fr8ph" />

            {/* Flash Icon */}
            <button onClick={() => setFlashOn(!flashOn)} className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-colors ${flashOn ? 'bg-[#F37024] text-white' : 'bg-black/30 text-white hover:bg-black/40'}`} data-id="element-2502" data-mp="1cep49egbw2ad">
              <ZapIcon className="w-5 h-5" fill={flashOn ? 'currentColor' : 'none'} data-id="element-2503" data-mp="2n6eofii8mlsm" />
            </button>
          </div>
          <p className={`text-[14px] text-center mb-8 ${textSec}`} data-id="element-2504" data-mp="2e8uoqe00wxk4">
            Align QR code within the frame to scan
          </p>

          <div className="w-full flex gap-4" data-id="element-2505" data-mp="29bcmt7kfbilm">
            <button onClick={() => {
          setQrMode(Math.random() > 0.5 ? 'static' : 'dynamic');
          if (qrMode === 'dynamic') setAmount('2500');
          navigateTo('entry');
        }} className={`flex-1 py-4 rounded-lg font-bold text-[14px] flex flex-col items-center justify-center gap-2 border ${cardBg} ${borderClr} ${textPri}`} data-id="element-2506" data-mp="24ayc1hrftujw">
              <StoreIcon className="w-6 h-6 text-[#F37024]" data-id="element-2507" data-mp="32ksfke5dirtu" />
              Enter Till Number
            </button>
            <button onClick={() => {
          // Selecting a QR from the gallery resolves the merchant + a
          // merchant-fixed amount, so jump straight to Review Payment.
          setQrMode('dynamic');
          setTillNumber('887654');
          setAmount('2500');
          navigateTo('review');
        }} className={`flex-1 py-4 rounded-lg font-bold text-[14px] flex flex-col items-center justify-center gap-2 border ${cardBg} ${borderClr} ${textPri}`} data-id="element-2508" data-mp="37i2a287mdtoj">
              <ImageIcon className="w-6 h-6 text-[#F37024]" data-id="element-2509" data-mp="2etmy5ujsbrhf" />
              Select from Gallery
            </button>
          </div>

          {onTransfer && <button onClick={onTransfer} className="mt-6 text-[14px] font-bold text-[#F37024] underline-offset-2 hover:underline" data-id="element-2510" data-mp="91q34lvnvg8q">
              Send to a person instead
            </button>}
        </div> : <div className="flex-1 flex flex-col px-6 pb-8" data-id="element-2511" data-mp="1tk0lwdd8v49w">
          <div className={`rounded-lg p-6 flex flex-col items-center border shadow-sm ${cardBg} ${borderClr}`} data-id="element-2512" data-mp="27adqq6uua2v0">
            {/* QR */}
            <div className="w-40 h-40 bg-white p-2 rounded-lg mb-5 flex items-center justify-center" data-id="element-2513" data-mp="22i47f9o8c11p">
              <QrCodeIcon className="w-full h-full text-black" data-id="element-2514" data-mp="2h6qiv0aqk8nn" />
            </div>

            <p className={`text-[18px] font-bold mb-0.5 ${textPri}`} data-id="element-2515" data-mp="2c8p1cjsykhea">
              Kashish Ahmed
            </p>
            <p className={`text-[13px] mb-5 ${textSec}`} data-id="element-2516" data-mp="2q8gll2a2iu0q">
              RAAST ID: 0300 1234567
            </p>

            {/* With Amount toggle */}
            <div className={`w-full rounded-lg px-4 py-3 flex items-center justify-between mb-5 ${isDark || isPlatinum ? 'bg-[#3A3A3C]' : 'bg-[#F5F3F1]'}`} data-id="element-2517" data-mp="vup27b1xsvs3">
              <div data-id="element-2518" data-mp="1ls15jq0i07o9">
                <p className={`text-[14px] font-medium ${textPri}`} data-id="element-2519" data-mp="1lsb07qwhn30a">
                  With Amount
                </p>
                <p className={`text-[11px] ${textSec}`} data-id="element-2520" data-mp="1gs6hrvc0ry7f">
                  For Making dynamic Payment
                </p>
              </div>
              <button onClick={() => setWithAmount(!withAmount)} className={`relative shrink-0 w-12 h-7 rounded-full transition-colors duration-300 ${withAmount ? 'bg-[#22C55E]' : isDark || isPlatinum ? 'bg-white/15' : 'bg-[#E5DDD7]'}`} aria-label="Toggle amount" data-id="element-2521" data-mp="1ct7fkihoafjo">
                <motion.span layout transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md" style={{
              left: withAmount ? 'calc(100% - 26px)' : '2px'
            }} data-id="element-2522" data-mp="1vpp3eotehvan" />
              </button>
            </div>

            <AnimatePresence initial={false} data-id="element-2523" data-mp="2qtalbez84kev">
              {withAmount && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="w-full overflow-hidden" data-id="element-2524" data-mp="2vqaqlno91j4x">
                  <div className="w-full mb-5 space-y-4" data-id="element-2525" data-mp="3jfv7m10ue6bk">
                    <div data-id="element-2526" data-mp="3jgeqpl28b0r6">
                      <label className={`text-[13px] font-medium mb-2 block ${isDark || isPlatinum ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2527" data-mp="3oeq1by0clfag">
                        Amount to Receive (PKR)
                      </label>
                      <input type="number" value={receiveAmount} onChange={e => setReceiveAmount(e.target.value)} placeholder="Enter amount" className={`w-full rounded-lg py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2528" data-mp="1a9vcomodvog" />
                    </div>
                    <div data-id="element-2529" data-mp="2h0cn9cv5r0n8">
                      <label className={`text-[13px] font-medium mb-2 block ${isDark || isPlatinum ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2530" data-mp="2gzt7a1p2imbu">
                        Validity Date
                      </label>
                      <input type="date" defaultValue={new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]} className={`w-full rounded-lg py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2531" data-mp="2l2z8guxbpnbg" />
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            <div className="w-full flex gap-3" data-id="element-2532" data-mp="2xl36w7ue46t2">
              <button className={`flex-1 py-3.5 rounded-lg font-medium text-[14px] border ${borderClr} ${textPri}`} data-id="element-2533" data-mp="2xjs3i94ux12h">
                Share QR
              </button>
              <button className={`flex-1 py-3.5 rounded-lg font-medium text-[14px] border ${borderClr} ${textPri}`} data-id="element-2534" data-mp="2om81uz3312rc">
                Save to Gallery
              </button>
            </div>
          </div>
        </div>}
    </motion.div>;
  const renderEntry = () => {
    const entryOptions: EntryOption[] = ['Till Code', 'VPA', 'Merchant ID', 'Free Text', 'Merchant Account Number'];
    return <motion.div key="entry" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2535" data-mp="3ka411la6mp6h">
        {renderHeader('Payment Details', () => navigateTo('scan', -1))}
        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2536" data-mp="35bs7vom0pyk2">
          <div className="space-y-6 mb-auto" data-id="element-2537" data-mp="31aq6p28jjcfg">
            {/* Entry Options */}
            <div className="flex flex-wrap gap-2" data-id="element-2538" data-mp="31ct7mhb8epyy">
              {entryOptions.map(opt => <button key={opt} onClick={() => setEntryOption(opt)} className={`px-4 py-2 rounded-full text-[12px] font-bold transition-colors border ${entryOption === opt ? 'bg-[#F37024] text-white border-[#F37024]' : `${cardBg} ${textSec} ${borderClr}`}`} data-id="element-2539" data-mp="2wif41rmf1cj7">
                  {opt}
                </button>)}
            </div>

            <div data-id="element-2540" data-mp="t3mzdzco0itp">
              <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2541" data-mp="t44s4m04cewv">
                {entryOption}
              </label>
              <input type="text" value={tillNumber} onChange={e => {
              const val = e.target.value;
              setTillNumber(val);
              // A Till Code resolves to a merchant-fixed amount: once a
              // valid code is entered, lock the amount and auto-fill a
              // merchant value greater than 100.
              if (entryOption === 'Till Code') {
                if (val.length > 3) {
                  setQrMode('dynamic');
                  setAmount('2500');
                } else {
                  setQrMode('static');
                  setAmount('');
                }
              }
            }} placeholder={`Enter ${entryOption}`} className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2542" data-mp="x3djxlblm2ps" />
            </div>

            {/* Pay To (Merchant Name) - Simulated */}
            {tillNumber.length > 3 && <div className={`p-4 rounded-2xl border ${cardBg} ${borderClr}`} data-id="element-2543" data-mp="3thz1oy8qbcqp">
                <p className={`text-[12px] font-medium uppercase tracking-wider ${textSec} mb-1`} data-id="element-2544" data-mp="3okfcx4eenhki">
                  Pay To
                </p>
                <p className={`text-[16px] font-bold ${textPri}`} data-id="element-2545" data-mp="2wspydh88tdly">
                  Al-Fatah Supermarket
                </p>
              </div>}

            {/* Pay From Dropdown */}
            <div className="relative z-20" data-id="element-2546" data-mp="35sd3oe43f3ka">
              <h3 className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2547" data-mp="35rbhpeljgwns">
                Pay From
              </h3>
              <button onClick={() => setShowPayFromDropdown(!showPayFromDropdown)} className={`w-full rounded-2xl p-4 flex items-center justify-between border shadow-sm ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.1)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2548" data-mp="q3l5uacny5ri">
                <div className="text-left" data-id="element-2549" data-mp="27x0rcab7q32w">
                  <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2550" data-mp="1u0nh9m90d3yg">
                    {payFromAccount.label}
                  </p>
                  <p className={`text-[13px] ${textSec}`} data-id="element-2551" data-mp="1yyypxtnkh8f3">
                    {payFromAccount.number}
                  </p>
                </div>
                <ChevronDownIcon className={`w-5 h-5 ${textSec}`} data-id="element-2552" data-mp="2qqebrlhaur49" />
              </button>

              <AnimatePresence data-id="element-2553" data-mp="2cyq4pwlwqsu5">
                {showPayFromDropdown && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className={`absolute top-[80px] left-0 right-0 rounded-2xl shadow-xl overflow-hidden border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.2)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2554" data-mp="2cwnbl3pgkjl7">
                    {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                  setPayFromAccount(acc);
                  setShowPayFromDropdown(false);
                }} className={`w-full text-left p-4 border-b last:border-0 ${isDark ? 'border-[rgba(243,112,36,0.1)] hover:bg-white/5' : 'border-[#F0E8E3] hover:bg-black/5'}`} data-id="element-2555" data-mp="vo14zuv8mbzt">
                        <p className={`font-bold ${textPri}`} data-id="element-2556" data-mp="7x5ind9jw8zy">{acc.label}</p>
                        <p className={`text-sm ${textSec}`} data-id="element-2557" data-mp="7ygjp5bm32nd">{acc.number}</p>
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div data-id="element-2558" data-mp="2jkh4fp8c2upr">
              <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2559" data-mp="2jjzbp2kvqyml">
                Amount (PKR){' '}
                {qrMode === 'dynamic' && <span className="text-[#F37024] font-normal ml-2" data-id="element-2560" data-mp="2ohhba2d0c1hx">
                    (Fixed by Merchant)
                  </span>}
              </label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" disabled={qrMode === 'dynamic'} className={`w-full rounded-lg py-4 px-4 text-[15px] font-medium border outline-none focus:border-[#F37024] ${qrMode === 'dynamic' ? isDark || isPlatinum ? 'bg-[#2A2A2C] text-[#9A9A9E] border-[rgba(255,255,255,0.08)] cursor-not-allowed' : 'bg-[#F2F2F2] text-[#818285] border-[#E5E5E5] cursor-not-allowed' : `${cardBg} ${textPri} ${borderClr}`}`} data-id="element-2561" data-mp="2y8lr14aibqdt" />
              <div className="flex justify-between mt-2 ml-1 mr-1" data-id="element-2562" data-mp="11pyf8tzqsfzy">
                <p className={`text-[12px] ${textSec}`} data-id="element-2563" data-mp="16mou5dzt95js">
                  Available Balance: PKR{' '}
                  {payFromAccount.balance.toLocaleString()}.00
                </p>
                {amount && <p className={`text-[12px] font-medium ${payFromAccount.balance - Number(amount) < 0 ? 'text-red-500' : textSec}`} data-id="element-2564" data-mp="1gecopnrnz2ph">
                    Remaining: PKR{' '}
                    {(payFromAccount.balance - Number(amount)).toLocaleString()}
                    .00
                  </p>}
              </div>
            </div>
          </div>
          <button onClick={() => navigateTo('review')} disabled={!tillNumber || !amount} className="h-[55px] w-[280px] mx-auto rounded-lg font-semibold text-[16px] bg-[#F37024] text-white shadow-md disabled:opacity-50 mt-6" data-id="element-2565" data-mp="1zauieaq2pcod">
            Continue
          </button>
        </div>
      </motion.div>;
  };
  const renderReview = () => <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2566" data-mp="2dgmnh8iilczu">
      {renderHeader('Review Payment', () => navigateTo('entry', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2567" data-mp="317jq1uof6xyf">
        <div className={`rounded-[24px] p-6 shadow-sm mb-6 border ${cardBg} ${borderClr}`} data-id="element-2568" data-mp="314xgjphyy8dk">
          <div className="flex flex-col items-center mb-8 text-center" data-id="element-2569" data-mp="2s6lhcz31e29i">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2570" data-mp="2s8oiae5q9ft0">
              <StoreIcon className="w-8 h-8 text-[#F37024]" data-id="element-2571" data-mp="1gsg9huzpd88w" />
            </div>
            <p className={`text-[28px] font-bold ${textPri}`} data-id="element-2572" data-mp="1brsmrliibdkh">
              Rs. {parseInt(amount || '0').toLocaleString()}
            </p>
            <p className={`text-[14px] mt-1 ${textSec}`} data-id="element-2573" data-mp="1da0thanergk4">
              To: Al-Fatah Supermarket
            </p>
          </div>
          <div className="space-y-4" data-id="element-2574" data-mp="1i67y9042im0h">
            <div className="flex justify-between" data-id="element-2575" data-mp="149m1xtjup586">
              <span className={textSec} data-id="element-2576" data-mp="14b5k5pp2hqcl">Pay From</span>
              <span className={`font-bold ${textPri}`} data-id="element-2577" data-mp="196upk9nrq1gt">
                {payFromAccount.number.slice(-4)}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2578" data-mp="uh6kqygdokoc">
              <span className={textSec} data-id="element-2579" data-mp="zarl2x5g1qqp">Merchant IBAN</span>
              <span className={`font-bold ${textPri}`} data-id="element-2580" data-mp="libgvt0t3mo7">
                PK36BOPU••••••••••1234
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2581" data-mp="pj50493b0dar">
              <span className={textSec} data-id="element-2582" data-mp="phk74nwn12f0">Transaction Fee</span>
              <span className={`font-bold ${textPri}`} data-id="element-2583" data-mp="blgfn2d5f868">Rs. 0</span>
            </div>
            <div className={`h-px w-full ${borderClr} my-2`} data-id="element-2584" data-mp="gi6whrws27sp" />
            <div className="flex justify-between" data-id="element-2585" data-mp="3t2uj8ji9my93">
              <span className={`font-bold ${textPri}`} data-id="element-2586" data-mp="3t3m34x2i37jj">Total Amount</span>
              <span className={`font-bold text-[#F37024]`} data-id="element-2587" data-mp="3o4j8m6k5cjpt">
                Rs. {parseInt(amount || '0').toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4 mb-auto" data-id="element-2588" data-mp="1w05bl2gpzal">
          {/* Save as Payee Toggle */}
          <div className="flex items-center justify-between" data-id="element-2589" data-mp="fwat2f8h6xrj">
            <span className={`text-[14px] font-bold ${textPri} ${qrMode === 'dynamic' ? 'opacity-50' : ''}`} data-id="element-2590" data-mp="fuhod5hfanm6">
              Save as Payee
            </span>
            <button onClick={() => setSavePayee(!savePayee)} disabled={qrMode === 'dynamic'} className={`relative shrink-0 w-12 h-7 rounded-full transition-colors duration-300 disabled:opacity-50 ${savePayee ? 'bg-[#F37024]' : isDark || isPlatinum ? 'bg-white/15' : 'bg-[#E5DDD7]'}`} data-id="element-2591" data-mp="pksu8xko36vp">
              <motion.span layout transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md" style={{
              left: savePayee ? 'calc(100% - 26px)' : '2px'
            }} data-id="element-2592" data-mp="ujdvn0bnnwlq" />
            </button>
          </div>

          <AnimatePresence data-id="element-2593" data-mp="2563ecz7qcufs">
            {savePayee && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} data-id="element-2594" data-mp="2a44zrqsmp5uf">
                <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2595" data-mp="3qhv2ou80ho2h">
                  Nickname
                </label>
                <input type="text" value={payeeNickname} onChange={e => setPayeeNickname(e.target.value)} placeholder="Enter nickname" className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2596" data-mp="1iwwe2i9wtrxu" />
              </motion.div>}
          </AnimatePresence>

          {/* Purpose Dropdown */}
          <div className="relative z-10" data-id="element-2597" data-mp="v6k7baqei18u">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2598" data-mp="v5qxg0w5avmh">
              Purpose
            </label>
            <button onClick={() => setShowPurposeDropdown(!showPurposeDropdown)} className={`w-full rounded-2xl py-4 px-4 flex items-center justify-between text-[15px] font-medium border ${cardBg} ${textPri} ${borderClr}`} data-id="element-2599" data-mp="r7bfibex8dfx">
              {purpose}
              <ChevronDownIcon className={`w-5 h-5 ${textSec}`} data-id="element-2600" data-mp="2gibv94mrssv0" />
            </button>
            <AnimatePresence data-id="element-2601" data-mp="2zngmsm267y8j">
              {showPurposeDropdown && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className={`absolute top-full left-0 right-0 mt-2 rounded-2xl shadow-xl overflow-hidden border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.2)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2602" data-mp="34gryb0hxh36s">
                  {['Merchant Payment', 'Family Support', 'Bill Payment', 'Salary', 'Business Payment', 'Loan Repayment', 'Gift', 'Other'].map(p => <button key={p} onClick={() => {
                setPurpose(p);
                setShowPurposeDropdown(false);
              }} className={`w-full text-left p-4 border-b last:border-0 ${isDark ? 'border-[rgba(243,112,36,0.1)] hover:bg-white/5' : 'border-[#F0E8E3] hover:bg-black/5'} ${purpose === p ? 'font-bold text-[#F37024]' : textPri}`} data-id="element-2603" data-mp="1vdx2jhbdfw1r">
                      {p}
                    </button>)}
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Comments */}
          <div data-id="element-2604" data-mp="1gqlqleba2a14">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2605" data-mp="1gq3m5uegopi4">
              Comments (Optional)
            </label>
            <div className="relative" data-id="element-2606" data-mp="sz6mph3urknr">
              <input type="text" value={comments} onChange={e => setComments(e.target.value.slice(0, 140))} placeholder="Add a note" className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2607" data-mp="o1o9vfmpslah" />
              <span className={`absolute right-4 top-4 text-[12px] ${textSec}`} data-id="element-2608" data-mp="cim1ltzetdfv">
                {comments.length}/140
              </span>
            </div>
          </div>
        </div>

        <button onClick={() => navigateTo('otp')} className="h-[55px] w-[280px] mx-auto rounded-lg font-semibold text-[16px] bg-[#F37024] text-white shadow-md mt-6" data-id="element-2609" data-mp="megox2ekvwoz">
          Pay Rs. {parseInt(amount || '0').toLocaleString()}
        </button>
      </div>
    </motion.div>;
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2610" data-mp="1556tqwnpltkh">
      {renderHeader('Enter OTP', () => navigateTo('review', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2611" data-mp="2ujzddcxi4vsp">
        <div className="mb-8 text-center" data-id="element-2612" data-mp="2pmfz4d0t0j0r">
          <p className={`text-[14px] ${textSec}`} data-id="element-2613" data-mp="2ppln1oxp5euw">
            We've sent a 6-digit code to your registered mobile number
          </p>
        </div>
        <div className="flex justify-between gap-2 mb-8" data-id="element-2614" data-mp="2fq4jpe5x3d23">
          {otp.map((digit, i) => <input key={i} id={`qr-otp-${i}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 rounded-xl text-center text-[24px] font-bold border-2 outline-none transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10 text-[#F37024]' : `${borderClr} ${cardBg} ${textPri} focus:border-[#F37024]/50`}`} data-id="element-2615" data-mp="3dbs4t2wxa8yb" data-mpi={i} />)}
        </div>
        <div className="text-center mb-auto" data-id="element-2616" data-mp="wx5al21phjhy">
          {timer > 0 ? <p className={`text-[14px] font-medium ${textSec}`} data-id="element-2617" data-mp="rxixqtpsi59o">
              Resend OTP in 0:{timer.toString().padStart(2, '0')}
            </p> : <button onClick={() => setTimer(30)} className="text-[14px] font-bold text-[#F37024]" data-id="element-2618" data-mp="15uo1gn216s1r">
              Resend OTP
            </button>}
        </div>
      </div>
    </motion.div>;
  const renderSuccess = () => <TransactionReceipt amount={parseInt(amount || '0')} toName="Al-Fatah Supermarket" toAccount="PK36BOPU••••••••••1234" fromName={payFromAccount.holder} fromAccount={payFromAccount.number} additionalInfo={[{
    label: 'Channel',
    value: 'RAAST'
  }, {
    label: 'Transaction Fee',
    value: 'PKR 0'
  }, {
    label: 'Purpose of Transfer',
    value: purpose || 'Other'
  }, ...(comments ? [{
    label: 'Comments',
    value: comments
  }] : [])]} onNavigateHome={onHome} data-id="element-2619" data-mp="1avc280g2an96" />;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2620" data-mp="249jfdtfpaivr">
      {step === 'scan' && renderScan()}
      {step === 'entry' && renderEntry()}
      {step === 'review' && renderReview()}
      {step === 'otp' && renderOtp()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}